// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Compras/ManifestacaoDfe.tsx
//
//  Iteração 6 · Fase 2 — Manifestação DFe (destinatário) à SEFAZ.
//  Permite enviar Ciência, Confirmação, Desconhecimento e Operação Não
//  Realizada para a chave NF-e vinculada ao recebimento; exibe histórico.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import {
  ArrowLeft, Send, Eye, CheckCircle2, XCircle, FileX, History, AlertCircle,
} from 'lucide-react';

import { useManifestacoes, useManifestar, useRecebimentoDetalhe } from '@/features/compras/hooks/useRecebimento';
import type { TipoManifestacaoDfe } from '@/features/compras/types';
import { getApiErrorMessage } from '@/lib/apiClient';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const TIPOS: {
  tipo:       TipoManifestacaoDfe;
  codigo:     number;
  titulo:     string;
  descricao:  string;
  icon:       React.ReactNode;
  variant:    'default' | 'secondary' | 'destructive' | 'outline';
  exigirJustificativa: boolean;
}[] = [
  {
    tipo: 'Ciencia',
    codigo: 210200,
    titulo: 'Ciência da Operação',
    descricao: 'Confirma que você está ciente da emissão da NF-e contra seu CNPJ. Não implica aceite.',
    icon: <Eye className="h-5 w-5" />,
    variant: 'secondary',
    exigirJustificativa: false,
  },
  {
    tipo: 'Confirmacao',
    codigo: 210210,
    titulo: 'Confirmação da Operação',
    descricao: 'Aceita formalmente a operação. Uso após receber a mercadoria e conferir.',
    icon: <CheckCircle2 className="h-5 w-5" />,
    variant: 'default',
    exigirJustificativa: false,
  },
  {
    tipo: 'Desconhecimento',
    codigo: 210220,
    titulo: 'Desconhecimento da Operação',
    descricao: 'Rejeita a NF-e: você não reconhece esta operação como sua.',
    icon: <XCircle className="h-5 w-5" />,
    variant: 'destructive',
    exigirJustificativa: false,
  },
  {
    tipo: 'OperacaoNaoRealizada',
    codigo: 210240,
    titulo: 'Operação Não Realizada',
    descricao: 'A operação foi acordada mas não ocorreu (entrega não realizada, cancelamento etc.). Justificativa obrigatória.',
    icon: <FileX className="h-5 w-5" />,
    variant: 'outline',
    exigirJustificativa: true,
  },
];

const ManifestacaoDfe = () => {
  const { id } = useParams<{ id: string }>();
  const recebimentoId = Number(id);
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: historico = [], isLoading } = useManifestacoes(recebimentoId);
  const { data: recebimento } = useRecebimentoDetalhe(recebimentoId);
  const { manifestar, isManifesting } = useManifestar();

  const [tipoSel, setTipoSel] = useState<typeof TIPOS[number] | null>(null);
  const [justificativa, setJustificativa] = useState('');

  const abrirConfirmacao = (tipo: typeof TIPOS[number]) => {
    setTipoSel(tipo);
    setJustificativa('');
  };

  const enviar = async () => {
    if (!tipoSel) return;
    if (tipoSel.exigirJustificativa && justificativa.trim().length < 15) {
      toast.error('Justificativa obrigatória (mínimo 15 caracteres).');
      return;
    }
    try {
      const res = await manifestar({
        recebimentoId,
        request: {
          tipo:          tipoSel.tipo,
          usuarioId:     user?.id ?? 0,
          justificativa: tipoSel.exigirJustificativa ? justificativa : undefined,
        },
      });
      if (res.sucesso) {
        toast.success(`${res.tipoEventoDescricao} enviada (cStat ${res.cStat}).`);
      } else {
        toast.message(`${res.tipoEventoDescricao} registrada.`, {
          description: res.xMotivo,
        });
      }
      setTipoSel(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
        </Button>
        <div>
          {/* USA-COM-19: nº do recebimento + NF + fornecedor (não o id da rota). */}
          <h1 className="text-3xl font-bold">
            Manifestação DFe{recebimento ? ` · Recebimento nº ${recebimento.numero}` : ''}
          </h1>
          {recebimento && (
            <p className="text-sm text-muted-foreground">
              {recebimento.numeroNf ? `NF ${recebimento.numeroNf}/${recebimento.serieNf ?? '—'} · ` : ''}
              {recebimento.fornecedorNome ?? '(fornecedor não encontrado)'}
            </p>
          )}
        </div>
      </div>
      <p className="text-muted-foreground">
        Envie um evento de manifestação do destinatário à SEFAZ para a NF-e vinculada a este recebimento.
        Todas as manifestações ficam registradas no histórico.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {TIPOS.map((t) => (
          <Card key={t.tipo} className="flex flex-col">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                {t.icon}
                {t.titulo}
                <Badge variant="outline" className="ml-auto text-xs">{t.codigo}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col justify-between gap-4">
              <p className="text-sm text-muted-foreground">{t.descricao}</p>
              <Button
                variant={t.variant}
                className="gap-2 self-end"
                onClick={() => abrirConfirmacao(t)}
              >
                <Send className="h-4 w-4" /> Enviar
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Histórico */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <History className="h-4 w-4" /> Histórico de manifestações
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Carregando…</p>
          ) : historico.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma manifestação enviada ainda.</p>
          ) : (
            <div className="rounded-md border overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/hora</TableHead>
                    <TableHead>Evento</TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead>cStat</TableHead>
                    <TableHead>Motivo</TableHead>
                    <TableHead>Protocolo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {historico.map((h) => {
                    const sucesso = h.codigoSituacao === 135 || h.codigoSituacao === 136 || h.codigoSituacao === 155;
                    return (
                      <TableRow key={h.id}>
                        <TableCell className="text-xs">
                          {new Date(h.dtEvento).toLocaleString('pt-BR')}
                        </TableCell>
                        <TableCell>{h.tipoEventoDescricao}</TableCell>
                        <TableCell className="font-mono text-xs">{h.tipoEvento}</TableCell>
                        <TableCell>
                          <Badge variant={sucesso ? 'default' : 'outline'}>
                            {h.codigoSituacao}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs max-w-xs truncate" title={h.descricaoSituacao ?? ''}>
                          {h.descricaoSituacao ?? '—'}
                        </TableCell>
                        <TableCell className="text-xs font-mono">
                          {h.protocolo > 0 ? h.protocolo : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Diálogo de confirmação */}
      <Dialog open={!!tipoSel} onOpenChange={(o) => !o && setTipoSel(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {tipoSel?.icon} {tipoSel?.titulo}
            </DialogTitle>
            <DialogDescription>{tipoSel?.descricao}</DialogDescription>
          </DialogHeader>

          {tipoSel?.exigirJustificativa && (
            <div className="space-y-1">
              <Label>Justificativa (mínimo 15 caracteres) *</Label>
              <Textarea
                rows={3}
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
                placeholder="Ex: entrega cancelada pelo fornecedor em 15/04, não houve movimentação"
              />
              <p className="text-xs text-muted-foreground">
                {justificativa.length}/15 caracteres
              </p>
            </div>
          )}

          <div className="rounded-md bg-yellow-500/10 border border-yellow-500/50 p-3 text-sm flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
            <span>
              Esta ação é registrada no histórico com seu usuário e será enviada à SEFAZ.
            </span>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setTipoSel(null)}>Cancelar</Button>
            <Button
              onClick={enviar}
              disabled={isManifesting}
              className="gap-2"
            >
              <Send className="h-4 w-4" />
              {isManifesting ? 'Enviando…' : 'Confirmar envio'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManifestacaoDfe;
